import { useState } from 'react';
import { Datagrid, FunctionField, List, useRecordContext, useTranslate } from 'react-admin';
import {
  Box,
  Button,
  Card,
  Dialog,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import dayjs from 'dayjs';
import { DashboardContent } from 'src/layouts/dashboard';
import { CustomBreadcrumbs } from 'src/components/custom-breadcrumbs';
import { PermissionButton } from 'src/routes/components/permission-button';
import { PermissionGuard } from 'src/routes/components/permission-guard';
import { EmptyContent } from 'src/components/empty-content';
import ErrorPage403 from 'src/pages/error/403';
import {
  CHAIN_NAME_BY_ID,
  XBIT_RELAY_WITHDRAWAL_PROVIDER_CONFIGS_PERMISSION_RESOURCE,
} from './constants';
import EditWithdrawalProviderConfig from './Edit';
import './init';

const ChainWithdrawalFeesPanel = () => {
  const t = useTranslate();
  const record = useRecordContext<any>();
  const fees = record?.chainWithdrawalFees ?? [];

  return (
    <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1, my: 1 }}>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        {t('module.xbitRelayWithdrawalProviderConfigs.chainWithdrawalFees')}
      </Typography>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 600 }}>
              {t('module.xbitRelayWithdrawalProviderConfigs.chainId')}
            </TableCell>
            <TableCell sx={{ fontWeight: 600 }}>
              {t('module.xbitRelayWithdrawalProviderConfigs.flatFee')} (USD)
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {fees.length ? (
            fees.map((fee: any) => (
              <TableRow key={fee.chainId}>
                <TableCell>
                  <Typography variant="body2">
                    {fee.chainId}
                    {CHAIN_NAME_BY_ID[String(fee.chainId)] && (
                      <Typography component="span" variant="body2" sx={{ color: 'text.secondary' }}>
                        {` (${CHAIN_NAME_BY_ID[String(fee.chainId)]})`}
                      </Typography>
                    )}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                    {fee.flatFee}
                  </Typography>
                </TableCell>
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={2} align="center">
                <Typography variant="body2" sx={{ color: 'text.secondary', py: 1 }}>
                  {t('module.xbitRelayWithdrawalProviderConfigs.noChainWithdrawalFees')}
                </Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </Box>
  );
};

const XbitRelayWithdrawalProviderConfigsList = () => {
  const t = useTranslate();
  const [editRecord, setEditRecord] = useState<any | null>(null);

  return (
    <DashboardContent>
      <PermissionGuard
        resource={XBIT_RELAY_WITHDRAWAL_PROVIDER_CONFIGS_PERMISSION_RESOURCE}
        action="read"
        fallback={<ErrorPage403 />}
      >
      <CustomBreadcrumbs
        heading={t('module.xbitRelayWithdrawalProviderConfigs.title')}
        links={[
          { name: t('resource.xbitRelay'), href: '#' },
          { name: t('module.xbitRelayWithdrawalProviderConfigs.title') },
          { name: t('resource.list') },
        ]}
      />

      <Card sx={{ mt: 3, pb: 2 }}>
        <List actions={false} empty={false} perPage={25} exporter={false}>
          <Box sx={{ overflow: 'auto' }}>
            <Datagrid
              rowClick={false}
              bulkActionButtons={false}
              empty={<EmptyContent />}
              expand={<ChainWithdrawalFeesPanel />}
              sx={{
                '& .RaDatagrid-headerCell': { fontWeight: 600 },
                '& .RaDatagrid-rowCell': { py: 1.25 },
              }}
            >
              <FunctionField
                label="module.xbitRelayWithdrawalProviderConfigs.provider"
                sortable={false}
                render={(record: any) => (
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {record.provider}
                  </Typography>
                )}
              />
              <FunctionField
                label="module.xbitRelayWithdrawalProviderConfigs.autoApprovalMaxAmount"
                sortable={false}
                render={(record: any) => (
                  <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                    {record.autoApprovalMaxAmount}
                  </Typography>
                )}
              />
              <FunctionField
                label="module.xbitRelayWithdrawalProviderConfigs.frequencyWindowMin"
                sortable={false}
                render={(record: any) => (
                  <Typography variant="body2">{record.frequencyWindowMin}</Typography>
                )}
              />
              <FunctionField
                label="module.xbitRelayWithdrawalProviderConfigs.frequencyMaxRequests"
                sortable={false}
                render={(record: any) => (
                  <Typography variant="body2">{record.frequencyMaxRequests}</Typography>
                )}
              />
              <FunctionField
                label="module.xbitRelayWithdrawalProviderConfigs.updatedAt"
                sortable={false}
                render={(record: any) =>
                  record.updatedAtUnixMs ? (
                    <Typography variant="body2">
                      {dayjs(Number(record.updatedAtUnixMs)).format('YYYY-MM-DD HH:mm:ss')}
                    </Typography>
                  ) : (
                    '-'
                  )
                }
              />
              <FunctionField
                label=""
                sortable={false}
                render={(record: any) => (
                  <PermissionButton
                    resource={XBIT_RELAY_WITHDRAWAL_PROVIDER_CONFIGS_PERMISSION_RESOURCE}
                    action="write"
                  >
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => setEditRecord(record)}
                    >
                      {t('actions.edit')}
                    </Button>
                  </PermissionButton>
                )}
              />
            </Datagrid>
          </Box>
        </List>
      </Card>

      <Dialog open={!!editRecord} onClose={() => setEditRecord(null)} maxWidth="sm" fullWidth>
        {editRecord && (
          <EditWithdrawalProviderConfig
            id={editRecord.provider}
            record={editRecord}
            onClose={() => setEditRecord(null)}
          />
        )}
      </Dialog>
      </PermissionGuard>
    </DashboardContent>
  );
};

export default XbitRelayWithdrawalProviderConfigsList;
